export type TFilterableContact = {
  partyType?: string | null
  hasBankDetails?: boolean
  ficaCompliance?: boolean | null
  hasLiveLease?: boolean
}

export const CONTACT_TAB_KEYS = ['all', 'owners', 'tenants', 'applicants', 'beneficiaries', 'team'] as const

export type TContactTabKey = typeof CONTACT_TAB_KEYS[number]

export type TContactFilterPill = {
  key: string
  label: string
  tabs?: TContactTabKey[]
  matches: (contact: TFilterableContact) => boolean
}

export type TContactFilterGroup = {
  /** Doubles as the query string param name. */
  key: string
  label: string
  pills: TContactFilterPill[]
}

/** One pill per group: the group's key maps to the selected pill's key, or to nothing. */
export type TContactFilterSelection = Record<string, string | undefined>

export const OUTSTANDING_GROUP_KEY = 'outstanding'

export const STATUS_GROUP_KEY = 'status'

/** Owners and tenants are the tabs where a contact holds a lease that can be live or not. */
const STATUS_TABS: TContactTabKey[] = ['owners', 'tenants']

export const CONTACT_FILTER_GROUPS: TContactFilterGroup[] = [
  {
    key: OUTSTANDING_GROUP_KEY,
    label: 'Needs attention',
    pills: [
      {
        key: 'no-bank-account',
        label: 'No bank account saved',
        tabs: ['owners', 'tenants', 'beneficiaries'],
        matches: contact => !contact.hasBankDetails,
      },
      {
        key: 'not-fica-compliant',
        label: 'Not FICA compliant',
        tabs: ['owners', 'tenants', 'applicants'],
        matches: contact => contact.ficaCompliance === false,
      },
    ],
  },
  {
    key: STATUS_GROUP_KEY,
    label: 'Status',
    pills: [
      {
        key: 'active',
        label: 'Active',
        tabs: STATUS_TABS,
        matches: contact => Boolean(contact.hasLiveLease),
      },
      {
        key: 'inactive',
        label: 'Inactive',
        tabs: STATUS_TABS,
        matches: contact => !contact.hasLiveLease,
      },
    ],
  },
  {
    key: 'contactType',
    label: 'Contact type',
    pills: [
      { key: 'natural-person', label: 'Natural person', matches: contact => contact.partyType === 'person' },
      { key: 'legal-entity', label: 'Legal entity', matches: contact => contact.partyType === 'company' },
    ],
  },
]

/**
 * The selection lives in the query string, but every contacts redirect pushes a bare path, which
 * drops it. Callers append this to the path they push so opening, closing or stepping into a
 * statement from a filtered list comes back to the same filtered list. Only the filter groups' own
 * params are carried — anything else on the URL (`redirectTo`) is left where it was.
 */
export const getContactFilterSearch = (search: string): string => {
  const current = new URLSearchParams(search)
  const carried = new URLSearchParams()

  CONTACT_FILTER_GROUPS.forEach(group => {
    const value = current.get(group.key)

    if (value) {
      carried.append(group.key, value)
    }
  })

  const query = carried.toString()

  return query ? `?${query}` : ''
}

const isPillOnTab = (pill: TContactFilterPill, tabKey: string): boolean =>
  !pill.tabs || pill.tabs.includes(tabKey as TContactTabKey)

export const getContactFilterGroups = (tabKey: string): TContactFilterGroup[] =>
  CONTACT_FILTER_GROUPS.map(group => ({
    ...group,
    pills: group.pills.filter(pill => isPillOnTab(pill, tabKey)),
  })).filter(group => group.pills.length > 0)

const getStatusPills = (tabKey: string): TContactFilterPill[] =>
  getContactFilterGroups(tabKey).find(group => group.key === STATUS_GROUP_KEY)?.pills ?? []

/**
 * Whether a tab shows the Status column. Keyed to the tabs the Status pills are offered on, so a
 * tab can never show a status it cannot be filtered by.
 */
export const hasStatusColumn = (tabKey: string): boolean => getStatusPills(tabKey).length > 0

/**
 * The Status column's label for a contact. Like the Outstanding labels, it is the label of
 * whichever pill the contact matches, so the column and the filter cannot drift apart. The pills
 * partition the tab, so every contact on a tab that has the column gets one.
 */
export const getContactStatusLabel = (contact: TFilterableContact, tabKey: string): string | undefined =>
  getStatusPills(tabKey).find(pill => pill.matches(contact))?.label

const getOutstandingPills = (tabKey: string): TContactFilterPill[] =>
  getContactFilterGroups(tabKey).find(group => group.key === OUTSTANDING_GROUP_KEY)?.pills ?? []

/**
 * Whether a tab shows the Needs attention column. The tab has to have outstanding pills at all
 * *and* at least one of its contacts has to be outstanding, so a tab where everyone is in order
 * ends the row at the contact number rather than carrying a column of blanks.
 *
 * Deliberately keyed to the whole tab rather than to the rows currently on screen: filtering by
 * contact type would otherwise make the column come and go, shifting the table under the user.
 */
export const hasOutstandingColumn = (tabKey: string, contacts: TFilterableContact[]): boolean =>
  getOutstandingPills(tabKey).some(pill => contacts.some(contact => pill.matches(contact)))

/**
 * Labels for the Outstanding table column. Deliberately derived from the same pills the Outstanding
 * filter uses, so the column and the filter can never disagree about what counts as outstanding.
 */
export const getOutstandingLabels = (contact: TFilterableContact, tabKey: string): string[] =>
  getOutstandingPills(tabKey)
    .filter(pill => pill.matches(contact))
    .map(pill => pill.label)

type TApplyOptions = {
  tabKey: string
  excludeGroupKey?: string
}

export const applyContactFilterPills = <T extends TFilterableContact>(
  contacts: T[],
  selection: TContactFilterSelection,
  { tabKey, excludeGroupKey }: TApplyOptions,
): T[] =>
  getContactFilterGroups(tabKey).reduce((remaining, group) => {
    if (group.key === excludeGroupKey) {
      return remaining
    }

    const selected = group.pills.find(pill => pill.key === selection[group.key])

    if (!selected) {
      return remaining
    }

    return remaining.filter(contact => selected.matches(contact))
  }, contacts)

export const countContactsForPill = (contacts: TFilterableContact[], pill: TContactFilterPill): number =>
  contacts.filter(pill.matches).length
